import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { MapPin } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DJWorldMap from "@/components/DJWorldMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DJs() {
  const [djs, setDjs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/djs`);
        setDjs(res.data.djs || []);
      } catch (e) {
        /* silent */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="relative min-h-screen" data-testid="djs-page">
      <Header />
      {/* Heading */}
      <section className="relative border-b border-zinc-800 px-4 md:px-10 pt-16 pb-12">
        <div className="text-xs tracking-[0.4em] text-zinc-300 uppercase mb-3">
          //_THE_BROADCASTERS
        </div>
        <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.85] tracking-tighter text-zinc-50 glitch">
          MEET THE <span className="text-red-600">DJs</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-sm md:text-base text-zinc-100 leading-relaxed">
          The humans behind the signal. Every show on Digital Revolution Radio
          is hosted by a real person who loves the music. No algorithms, no
          interns, no AI. Just rock fans broadcasting to other rock fans.
        </p>
      </section>

      {/* Global Broadcast Map */}
      {!loading && djs.length > 0 && (
        <section className="border-b border-zinc-800 px-4 md:px-10 py-10">
          <DJWorldMap djs={djs} />
        </section>
      )}

      {/* Grid */}
      <section className="px-4 md:px-10 py-10">
        {loading ? (
          <div className="text-zinc-300 text-sm tracking-widest uppercase">
            //_loading broadcasters...
          </div>
        ) : (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 border-l border-t border-zinc-800"
            data-testid="djs-grid"
          >
            {djs.map((dj) => (
              <DJCard key={dj.slug} dj={dj} />
            ))}
          </div>
        )}
        <p className="mt-8 text-xs text-zinc-600 tracking-widest uppercase">
          //_{djs.length} broadcasters · more profiles being added
        </p>
      </section>
      <Footer />
    </div>
  );
}

function DJCard({ dj }) {
  const [imgFailed, setImgFailed] = useState(false);
  const initials = dj.name
    .split(/[\s&]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  const showImage = !!dj.photo && !imgFailed;

  return (
    <Link
      to={`/djs/${dj.slug}`}
      className="group block border-r border-b border-zinc-800 bg-zinc-950 hover:bg-zinc-900 transition-colors"
      data-testid={`dj-card-${dj.slug}`}
    >
      <div className="aspect-square overflow-hidden border-b border-zinc-800 bg-zinc-900 relative">
        {showImage ? (
          <img
            src={dj.photo}
            alt={dj.name}
            loading="lazy"
            onError={() => setImgFailed(true)}
            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-300"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-950 to-zinc-800"
            data-testid={`dj-photo-fallback-${dj.slug}`}
          >
            <span className="font-display text-7xl text-zinc-600 select-none">
              {initials}
            </span>
          </div>
        )}
        <div className="absolute top-2 left-2 text-[10px] tracking-widest uppercase bg-black/80 px-2 py-1 text-red-500">
          //_DJ
        </div>
      </div>
      <div className="p-4">
        <div className="font-display text-xl md:text-2xl leading-tight tracking-tight text-zinc-50">
          {dj.name}
        </div>
        <div className="mt-1 text-xs text-zinc-300 tracking-widest uppercase flex items-center gap-1">
          {dj.location && (
            <>
              <MapPin className="w-3 h-3" /> {dj.location}
            </>
          )}
        </div>
        {dj.tagline && (
          <div className="mt-2 text-xs text-zinc-100 leading-snug line-clamp-2">
            {dj.tagline}
          </div>
        )}
      </div>
    </Link>
  );
}
